import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import axios from 'axios';

const KubernetesServicesPage = () => {
    const { selectedAKSCluster } = useSelector((state: RootState) => state.monitoring);

    const [services, setServices] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [filter, setFilter] = useState<any>('');

    useEffect(() => {
        const fetchServices = async () => {
            try {
                setLoading(true);
                const response = await axios.get('/api/monitoring/services');
                setServices(response.data.data);
                setLoading(false);
            } catch (error) {
                console.error('Error fetching services:', error);
                setLoading(false);
            }
        };

        fetchServices();
    }, []);

    // Filter by selected AKS cluster from sidebar
    const clusterFilteredServices = selectedAKSCluster
        ? services.filter((service: any) => service.cluster === selectedAKSCluster.name)
        : services;

    // Apply text filter on top of cluster filter
    const filteredServices = clusterFilteredServices.filter((service: any) =>
        service.name.toLowerCase().includes(filter.toLowerCase()) ||
        service.namespace.toLowerCase().includes(filter.toLowerCase()) ||
        service.cluster.toLowerCase().includes(filter.toLowerCase()) ||
        service.type.toLowerCase().includes(filter.toLowerCase())
    );

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                    <span className="ml-3 text-gray-600 dark:text-gray-400">Loading services...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Kubernetes Services</h1>
                    {selectedAKSCluster && (
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Filtered by: {selectedAKSCluster.name}
                        </p>
                    )}
                </div>
                {selectedAKSCluster && (
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                        Showing {filteredServices.length} of {services.length} total services
                    </div>
                )}
            </div>

            <div className="mb-6">
                <input
                    type="text"
                    placeholder="Filter by name, namespace, cluster or type..."
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md w-full bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                />
            </div>

            <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Cluster</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Namespace</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Ports</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Endpoints</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {filteredServices.map((service: any, index: number) => (
                            <tr key={`${service.cluster}-${service.namespace}-${service.name}-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900 dark:text-white">{service.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <Link
                                        to={`/kubernetes/clusters/${service.cluster}`}
                                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                                    >
                                        {service.cluster}
                                    </Link>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">{service.namespace}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${service.type === 'LoadBalancer'
                                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                        : service.type === 'NodePort'
                                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                                            : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                        }`}>
                                        {service.type}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                    {service.ports && service.ports.length > 0 ? (
                                        <div className="space-y-1">
                                            {service.ports.map((port: any, idx: number) => (
                                                <div key={idx}>
                                                    {port.port}{port.targetPort ? `:${port.targetPort}` : ''}/{port.protocol}
                                                </div>
                                            ))}
                                        </div>
                                    ) : '�'}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-gray-500 dark:text-gray-400">
                                    {service.endpoints && service.endpoints.length > 0 ? (
                                        <div className="space-y-1">
                                            {service.endpoints.map((endpoint: any, idx: number) => (
                                                <div key={idx}>
                                                    {endpoint.ip || endpoint.hostname || (endpoint.nodePort ? `NodePort:${endpoint.nodePort}` : '�')}
                                                </div>
                                            ))}
                                        </div>
                                    ) : '�'}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredServices.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        {filter ? 'No services match your filter' :
                            selectedAKSCluster ? `No services found in ${selectedAKSCluster.name}` :
                                'No services found'}
                    </div>
                )}
            </div>
        </div>
    );
};

export default KubernetesServicesPage;